import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { TextInput } from '@repo/design-system/components/ui/text-input';
import { Type } from '@repo/design-system/components/ui/type';
import { generateUniqueId } from '@repo/design-system/utils/accessibility';
import { cva, cx } from '@repo/panda/css';
import type { RecipeVariantProps } from '@repo/panda/types';
import type React from 'react';
import { forwardRef, useCallback, useEffect, useRef } from 'react';

const colorPickerStyles = cva({
    base: {},
    variants: {
        fullWidth: {
            true: { width: 'full' },
            false: { width: 'fit' },
        },
    },
    defaultVariants: {
        fullWidth: false,
    },
});

const colorInputRowStyles = cva({
    base: {
        display: 'flex',
        alignItems: 'center',
        gap: '8',
        transition: 'all',
        position: 'relative',
    },
});

const colorSwatchStyles = cva({
    base: {
        cursor: 'pointer',
        transition: 'all',
        position: 'relative',
        borderRadius: '8',
        appearance: 'none',
        border: 'none',
        padding: '0',
        margin: '0',
        '&::-webkit-color-swatch-wrapper': {
            borderRadius: '8',
            padding: '0',
            margin: '0',
            border: 'none',
        },
        '&::-webkit-color-swatch': {
            borderRadius: '8',
            padding: '0',
            margin: '0',
            border: 'none',
        },
    },
    variants: {
        size: {
            sm: {
                maxWidth: '[32px]',
                minWidth: '[32px]',
                height: '[32px]',
            },
            md: {
                height: '[36px]',
                maxWidth: '[36px]',
                minWidth: '[36px]',
            },
            lg: {
                height: '[40px]',
                maxWidth: '[40px]',
                minWidth: '[40px]',
            },
        },
    },
    defaultVariants: {
        size: 'md',
    },
});

export type ColorPickerProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> &
    RecipeVariantProps<typeof colorPickerStyles> & {
        label?: string;
        value: string;
        onChange: (color: string) => void;
        placeholder?: string;
        disabled?: boolean;
        required?: boolean;
        size?: 'sm' | 'md' | 'lg';
        variant?: 'dark' | 'light';
        error?: string;
        helperText?: string;
    };

export const ColorPicker = forwardRef<HTMLDivElement, ColorPickerProps>(
    (
        {
            label,
            value,
            onChange,
            placeholder = '#000000',
            disabled = false,
            required = false,
            size = 'md',
            variant = 'dark',
            error,
            helperText,
            fullWidth = false,
            className,
            ...props
        },
        ref,
    ) => {
        // Ensure the value is a valid hex color, fallback to black
        const normalizedValue = value.startsWith('#') ? value : `#${value}`;
        const isValidHex = /^#[0-9A-Fa-f]{6}$/.test(normalizedValue);
        const displayColor = isValidHex ? normalizedValue : '#000000';

        // Debouncing logic for color picker
        const debounceTimeoutRef = useRef<number | null>(null);
        const pendingColorRef = useRef<string | null>(null);

        const debouncedOnChange = useCallback(
            (newColor: string) => {
                pendingColorRef.current = newColor;

                if (debounceTimeoutRef.current) {
                    window.clearTimeout(debounceTimeoutRef.current);
                }

                debounceTimeoutRef.current = window.setTimeout(() => {
                    if (pendingColorRef.current) {
                        onChange(pendingColorRef.current);
                        pendingColorRef.current = null;
                    }
                }, 100); // 100ms debounce delay
            },
            [onChange],
        );

        // Cleanup timeout on unmount
        useEffect(() => {
            return () => {
                if (debounceTimeoutRef.current) {
                    window.clearTimeout(debounceTimeoutRef.current);
                }
            };
        }, []);

        const handleColorChange = (newColor: string) => {
            debouncedOnChange(newColor);
        };

        const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            let newValue = e.target.value;

            // Auto-add # if not present
            if (newValue && !newValue.startsWith('#')) {
                newValue = `#${newValue}`;
            }

            // Don't debounce text input changes for immediate feedback
            onChange(newValue);
        };

        const colorPickerId = generateUniqueId('color-picker');
        const labelId = generateUniqueId('color-picker-label');

        return (
            <div ref={ref} className={cx(colorPickerStyles({ fullWidth }), className)} {...props}>
                {label && (
                    <Type id={labelId} aria-label={label} variant="inputHint">
                        {label}
                    </Type>
                )}

                <FlexRow gap="4" className={colorInputRowStyles.raw({ variant })}>
                    <input
                        aria-labelledby={labelId}
                        type="color"
                        id={colorPickerId}
                        value={displayColor}
                        onChange={e => handleColorChange(e.target.value)}
                        disabled={disabled}
                        className={colorSwatchStyles({ size })}
                        aria-label={`${label || 'Color'} picker`}
                    />
                    <TextInput
                        aria-labelledby={labelId}
                        value={normalizedValue}
                        onChange={handleTextChange}
                        placeholder={placeholder}
                        size={size}
                        variant={variant}
                        required={required}
                        disabled={disabled}
                        errorText={error}
                        helperText={helperText}
                        fullWidth={fullWidth}
                    />
                </FlexRow>
            </div>
        );
    },
);
